<template>
    <benefits-modal name="tiers">
        <tab-bar
            class="tiers-tabs"
            :items="tabs"
            :value="activeTier && activeTier.id"
            color="#384250"
            @select="openTiers"
        />

        <main class="tiers" v-if="activeTier">
            <section class="tiers-card" :style="{ background: activeTier.color, color: activeTier.textColor }">
                <span class="tiers-member" v-if="activeTier.isMember" v-lang="'dashboard-tiersMember'"></span>

                <div class="tiers-card-head">
                    <span class="tiers-card-icon">
                        <tier-icon :tier="activeTier" />
                    </span>

                    <div class="tiers-card-info">
                        <h1 class="tiers-card-name">{{ activeTier.name }}</h1>
                        <rich-text
                            class="tiers-card-description"
                            v-if="activeTier.description"
                            :content="activeTier.description"
                        />
                    </div>
                </div>

                <template v-if="activeTier.becomingDescription">
                    <hr class="tiers-card-divider" />

                    <div class="tiers-card-become">
                        <h2 class="tiers-card-become-title">{{ t("dashboard-tiersBecome", { name: activeTier.name }) }}</h2>
                        <rich-text class="tiers-card-become-body" :content="activeTier.becomingDescription" />
                    </div>
                </template>
            </section>

            <section class="tiers-rewards">
                <h2 class="tiers-rewards-title" v-lang="'dashboard-tiersExclusiveRewards'"></h2>

                <p class="tiers-rewards-empty" v-if="!tierRewards.length" v-lang="'dashboard-tiersNoExclusiveRewards'"></p>

                <reward-card
                    v-for="reward in tierRewards"
                    :key="reward.id"
                    :reward="reward"
                    :balance="balance"
                    :is-logged="isLogged"
                    :tiers="tiers"
                    selectable
                    @select="openReward"
                />
            </section>
        </main>
    </benefits-modal>
</template>

<script>
import { mapMutations, mapState } from "vuex";

require("../points/points.store");
import BenefitsModal from "../../shared/modal-shell";
import TabBar from "../../shared/tab-bar";
import TierIcon from "../../shared/tier-icon";
import RichText from "../../shared/rich-text";
import RewardCard from "../points/points-reward-card";

export default {
    name: "TiersModal",
    components: { BenefitsModal, TabBar, TierIcon, RichText, RewardCard },
    computed: {
        ...mapState({
            client: (state) => state.client,
            token: (state) => state.token,
            tiers: (state) => state.dashboard.tiers,
            rewards: (state) => state.dashboard.rewards,
            balance: (state) => state.dashboard.balance,
            tierId: (state) => state.tierId,
        }),
        isLogged() {
            return !!(this.client && this.client.id && this.token);
        },
        sortedTiers() {
            return this.tiers.slice().sort((a, b) => a.position - b.position);
        },
        tabs() {
            return this.sortedTiers.map((tier) => ({ id: tier.id, label: tier.name }));
        },
        activeTier() {
            const memberTiers = this.sortedTiers.filter((tier) => tier.isMember);

            return (
                this.sortedTiers.filter((tier) => tier.id === this.tierId)[0] ||
                memberTiers[memberTiers.length - 1] ||
                this.sortedTiers[0] ||
                null
            );
        },
        tierRewards() {
            if (!this.activeTier) {
                return [];
            }

            return this.rewards.filter((reward) => (reward.tierIds || []).includes(this.activeTier.id));
        },
    },
    methods: {
        ...mapMutations(["openTiers"]),
        openReward(reward) {
            this.$store.commit("openReward", reward.id);
        },
    },
};
</script>

<style lang="scss">
.tiers-tabs {
  width: 100%;
}

.tiers {
  flex: 1 1 auto;
  width: 100%;
  max-width: 30rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 1rem 3rem;

  @media (min-width: 48rem) {
    max-width: 37.5rem;
    padding: 2rem 1rem 3rem;
  }
}

.tiers-card {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: 1.5rem;
  filter: drop-shadow(0 15px 12px rgba(0, 0, 0, 0.15));
}

.tiers-member {
  align-self: center;
  padding: 0.5rem;
  border-radius: 6.25rem;
  background: #fcfcfd;
  color: #384250;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
}

.tiers-card-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tiers-card-icon {
  flex: 0 0 auto;
  display: flex;
  padding: 0.5rem;
  font-size: 5.375rem;
  line-height: 1;
  filter: drop-shadow(0 5px 23px rgba(0, 0, 0, 0.25));

  .tier-icon {
    font-size: inherit;
  }
}

.tiers-card-info {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tiers-card-name {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.tiers-card-description {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;

  * {
    font-size: inherit;
  }
}

.tiers-card-divider {
  width: 100%;
  margin: 0;
  border: none;
  border-top: 1px solid currentColor;
  opacity: 0.3;
}

.tiers-card-become {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tiers-card-become-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.tiers-card-become-body {
  font-size: 0.875rem;
  line-height: 1.3;

  * {
    font-size: inherit;
  }
}

.tiers-rewards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tiers-rewards-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.3;
  color: #384250;
}

.tiers-rewards-empty {
  margin: 0;
  padding: 1.5rem 0;
  font-size: 0.875rem;
  color: #6c737f;
  text-align: center;
}
</style>
